import React, {
    ReactElement,
    useEffect,
    useLayoutEffect,
    useRef,
    useState
} from 'react'
import cn from 'classnames'
import { LinkItem, SubCategoryList } from '..'
import { RichContent } from '@corratech/pylot-rich-content'
import s from '@commonStyles/menus/MenuItem.module.scss'
import type { CategoryTree, Maybe } from '@pylot-data/pylotschema'
import { SubmenuMode, useStoreConfig } from 'config'
import {
    getMenuItemSlug,
    getMenuItemStableClassNames
} from '../../utils/getMenuItemStableClassNames'
import { isEspaciosMegaMenu, toMegaMenuCss } from '../../utils/megamenuCategory'
import EspaciosCasaMegaPanel from './EspaciosCasaMegaPanel'
// TODO: descomentar cuando PRODUCTOS, NUEVOS, NIÑOIDEAS y OFERTAS
// vuelvan a usar el panel nuevo en lugar del menú de master.
// import CategoryMegaPanel from './CategoryMegaPanel'

type MenuItemProps = {
    item: Maybe<CategoryTree>
    // Lo usa CategoryMegaPanel cuando se descomente el panel nuevo.
    siblingItems?: Maybe<CategoryTree>[]
    isMenuOpen?: boolean
    onMenuOpenChange?: (open: boolean) => void
}

export const MenuItem = ({
    item,
    isMenuOpen = false,
    onMenuOpenChange
}: MenuItemProps): ReactElement | null => {
    const { category_is_link } = item || { category_is_link: 0 }
    const { base } = useStoreConfig()
    const slug = item ? getMenuItemSlug(item) : undefined
    const isEspacios = isEspaciosMegaMenu(item)
    const [localOpen, setLocalOpen] = useState(false)
    const itemRef = useRef<HTMLLIElement>(null)
    const controlled = typeof onMenuOpenChange === 'function'
    const isOpen = controlled ? isMenuOpen : localOpen
    const setIsOpen = (next: boolean) => {
        if (controlled) onMenuOpenChange(next)
        else setLocalOpen(next)
    }

    const hasCmsPanel =
        !isEspacios &&
        base.menu.submenu === SubmenuMode.BLOCK &&
        Boolean(item?.cms_block?.content)

    useEffect(() => {
        if (controlled || !isOpen) return

        const onPointerDown = (event: MouseEvent) => {
            const target = event.target
            if (!(target instanceof Node)) return
            if (itemRef.current?.contains(target)) return
            setIsOpen(false)
        }
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setIsOpen(false)
        }

        document.addEventListener('mousedown', onPointerDown)
        document.addEventListener('keydown', onKeyDown)
        return () => {
            document.removeEventListener('mousedown', onPointerDown)
            document.removeEventListener('keydown', onKeyDown)
        }
    }, [controlled, isOpen])

    useLayoutEffect(() => {
        if (!isOpen || !hasCmsPanel) return
        const itemEl = itemRef.current
        const panel = itemEl?.querySelector<HTMLElement>('.menuCmsContent')
        const header = document.getElementById('navbar')?.parentElement
        if (!itemEl || !panel || !header) return

        const syncTop = () => {
            const top =
                header.getBoundingClientRect().bottom -
                itemEl.getBoundingClientRect().top
            panel.style.top = `${Math.round(top)}px`
        }

        syncTop()
        window.addEventListener('resize', syncTop)
        return () => {
            window.removeEventListener('resize', syncTop)
            panel.style.top = ''
        }
    }, [hasCmsPanel, isOpen])

    if (!item || !item.include_in_menu) return null

    if (isEspacios) {
        return (
            <li
                ref={itemRef}
                className={cn(
                    s['megamenu-cms-item'],
                    getMenuItemStableClassNames(item),
                    { 'is-open': isOpen }
                )}
                data-menu-url-key={slug}
            >
                <button
                    type="button"
                    aria-expanded={isOpen}
                    aria-haspopup="true"
                    style={toMegaMenuCss(item.megamenu_font_styles)}
                    onClick={() => setIsOpen(!isOpen)}
                >
                    {item.name}
                </button>
                {isOpen ? (
                    <EspaciosCasaMegaPanel
                        navChildren={item.children}
                        onNavigate={() => setIsOpen(false)}
                    />
                ) : null}
            </li>
        )
    }

    return (
        <li
            ref={itemRef}
            className={cn(
                s['megamenu-cms-item'],
                getMenuItemStableClassNames(item),
                { 'is-open': isOpen }
            )}
            data-menu-url-key={slug}
        >
            {hasCmsPanel ? (
                <button
                    type="button"
                    aria-expanded={isOpen}
                    aria-haspopup="true"
                    style={toMegaMenuCss(item.megamenu_font_styles)}
                    onClick={() => setIsOpen(!isOpen)}
                >
                    {item.name}
                </button>
            ) : category_is_link ? (
                <LinkItem
                    url={item.external_url || `/categoria/${item.url_path}`}
                    name={String(item.name)}
                    wrapName={false}
                />
            ) : (
                <span>{item.name}</span>
            )}
            {base.menu.submenu === SubmenuMode.BLOCK &&
                item?.cms_block?.content && (
                    <RichContent
                        html={item.cms_block.content}
                        classes={{ root: 'menuCmsContent' }}
                        isLazyLoad={false}
                    />
                )}
            {base.menu.submenu === SubmenuMode.SUBCATEGORY &&
                item.children &&
                item.children.length > 0 && (
                    <div className={s['sub-category']}>
                        <SubCategoryList
                            item={item}
                            maxItems={base.menu.submenu_maxitems ?? undefined}
                        />
                    </div>
                )}
            {/* TODO: panel nuevo de categorías. Descomentar el import de
                CategoryMegaPanel y este bloque, y volver a recibir siblingItems.
                Reemplaza el link/CMS de master para PRODUCTOS, NUEVOS,
                NIÑOIDEAS y OFERTAS.
            {isOpen ? (
                <CategoryMegaPanel
                    item={item}
                    siblingItems={siblingItems}
                    onNavigate={() => setIsOpen(false)}
                />
            ) : null}
            */}
        </li>
    )
}
